import { useState, ChangeEvent, useEffect } from "react";
import { Dropdown, Button, FormControl } from "react-bootstrap";

import { BlueText } from "components/common";
import CustomDropdownToggle from "components/common/CustomDropdownToggle";
import CustomDropdownMenu from "components/common/CustomDropdownMenu";
import CustomFormCheck from "components/common/CustomFormCheck";

type ActuatorTypeDropdownProps = {
    actuatorTypes: { id: string; name: string }[];
    selected: string[];
    onChange: (selected: string[]) => void;
    className?: string;
};

export const ActuatorTypeDropdown = ({
    actuatorTypes,
    selected,
    onChange,
    ...restProps
}: ActuatorTypeDropdownProps) => {
    const [searchText, setSearchText] = useState("");
    const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
    const [filteredList, setFilteredList] = useState<any[]>([]);
    const [show, setShow] = useState(false);

    const updateFilteredList = (
        allTypes: { id: string; name: string }[],
        selectedIds: string[],
        search: string
    ) => {
        const baseList = allTypes.filter(
            ({ id }) => !selectedIds.includes(id)
        );
        const value = search.toLowerCase();
        if (!value) {
            setFilteredList(baseList);
            return;
        }
        setFilteredList(
            baseList.filter(({ name }) =>
                name.toLowerCase().includes(value)
            )
        );
    };

    const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchText(value);
        updateFilteredList(actuatorTypes, selectedTypes, value);
    };

    const selectType = (e: ChangeEvent<HTMLInputElement>) => {
        const { checked, value } = e.target;
        let types = selectedTypes.filter((t) => t !== value);

        if (checked) {
            types.push(value);
        }

        setSelectedTypes(types);
    };

    const clearFilter = () => {
        setSelectedTypes([]);
        onChange([]);
        setSearchText("");
        updateFilteredList(actuatorTypes, [], "");
        setShow(false);
    };

    const applyFilter = () => {
        onChange([...selectedTypes]);
        setShow(false);
    };

    const handleOpenMenu = (isOpen: boolean) => {
        setShow(isOpen);
        setSelectedTypes(selected);
        const nextSearch = "";
        setSearchText(nextSearch);
        // When opening, show only unselected actuator types in the list (no search)
        updateFilteredList(actuatorTypes, selected, nextSearch);
    };

    useEffect(() => {
        // Keep filtered list in sync when actuator types, selection, or search text change
        updateFilteredList(actuatorTypes, selectedTypes, searchText);
    }, [actuatorTypes, selectedTypes, searchText]);

    useEffect(() => {
        setSelectedTypes(selected);
    }, [selected]);

    return (
        <Dropdown show={show} onToggle={handleOpenMenu} {...restProps}>
            <CustomDropdownToggle>
                <i className="material-icons-outlined mr-2">filter_alt</i>
                <span title="Filter">Filter</span>
                {selectedTypes.length > 0 && <i className="dot ml-2" />}
                <i className="material-icons down ml-2">
                    keyboard_arrow_down
                </i>
            </CustomDropdownToggle>

            <CustomDropdownMenu className="px-3 py-3">
                <h6 className="">Filter</h6>
                <FormControl
                    className="search mt-3 mb-2"
                    placeholder="Search for actuator types"
                    aria-label="search-dashboard"
                    onChange={handleSearch}
                    value={searchText}
                />
                {selectedTypes.length > 0 && (
                    <div>
                        <BlueText className="d-block mb-2">
                            Selected Types
                        </BlueText>
                        {selectedTypes.map((type) => (
                            <CustomFormCheck
                                key={type}
                                className="mb-2"
                                name="selected-actuator"
                                value={type}
                                checked={true}
                                custom
                                onChange={selectType}
                                id={type}
                                label={<span className="ml-2">{type}</span>}
                            />
                        ))}
                    </div>
                )}
                <BlueText className="d-block mb-2">Actuator Types</BlueText>
                <div className="check-list">
                    {filteredList.map(({ id, name }) => (
                        <CustomFormCheck
                            key={id}
                            className="mb-2"
                            name="actuator-type"
                            value={id}
                            checked={selectedTypes.includes(id)}
                            custom
                            onChange={selectType}
                            id={id}
                            label={<span className="ml-2">{name}</span>}
                        />
                    ))}
                </div>
                <div className="d-flex justify-content-between mt-3">
                    <Button
                        variant="dark"
                        size="sm"
                        className="px-md-3"
                        onClick={clearFilter}
                    >
                        CLEAR
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={applyFilter}
                    >
                        APPLY
                    </Button>
                </div>
            </CustomDropdownMenu>
        </Dropdown>
    );
};
